/*! Kei & Co. — HEIC/HEIF safety net for the photo upload field.
 *
 *  Problem observed 2026-10-02 on shopkeiandco.com:
 *  FilePond (used by the Mini: Image Upload app) accepts a .heic file,
 *  fires `addfilestart`, and then discards it. No item, no error event,
 *  no message. The customer believes the photo was attached and it was not.
 *
 *  Fix: intercept the file BEFORE FilePond sees it, convert HEIC/HEIF to
 *  JPEG in the customer's browser, and hand the JPEG to FilePond.
 *  If conversion is impossible, say so out loud instead of losing the file.
 *
 *  Risk posture: this code only takes over when a HEIC/HEIF is detected
 *  synchronously (extension or MIME). Every other file goes through the
 *  app untouched. If this script throws, only HEIC is affected — and HEIC
 *  is already broken without it. Never worse than the status quo.
 */
(function () {
  'use strict';

  var LIB = (window.KEI_HEIC_LIB_URL || '');
  var SEL_INPUT = 'input.filepond--browser';
  var SEL_ROOT = '.filepond--root';
  var libPromise = null;

  /* ---------- detection ---------------------------------------------- */

  // Chrome reports application/octet-stream for .heic, so MIME alone is
  // not enough. Extension first (that is what real files carry), MIME second.
  function looksHeic(f) {
    if (!f) return false;
    if (/\.(heic|heif)$/i.test(f.name || '')) return true;
    return /^image\/hei[cf]/i.test(f.type || '');
  }

  /* ---------- conversion ---------------------------------------------- */

  function loadLib() {
    if (libPromise) return libPromise;
    libPromise = new Promise(function (resolve, reject) {
      if (window.heic2any) { resolve(window.heic2any); return; }
      if (!LIB) { reject(new Error('no converter url')); return; }
      var s = document.createElement('script');
      s.src = LIB;
      s.async = true;
      s.onload = function () {
        window.heic2any ? resolve(window.heic2any) : reject(new Error('converter missing'));
      };
      s.onerror = function () { reject(new Error('converter failed to load')); };
      document.head.appendChild(s);
    });
    return libPromise;
  }

  function encodeCanvas(bmp) {
    var c = document.createElement('canvas');
    c.width = bmp.width;
    c.height = bmp.height;
    c.getContext('2d').drawImage(bmp, 0, 0);
    return new Promise(function (resolve, reject) {
      c.toBlob(function (b) { b ? resolve(b) : reject(new Error('encode failed')); },
               'image/jpeg', 0.92);
    });
  }

  function toJpeg(file) {
    // Safari and iOS decode HEIC natively: no download needed there.
    return Promise.resolve()
      .then(function () { return createImageBitmap(file); })
      .then(encodeCanvas)
      .catch(function () {
        // Everywhere else: pull the decoder, once, only when actually needed.
        return loadLib()
          .then(function (h) { return h({ blob: file, toType: 'image/jpeg', quality: 0.92 }); })
          .then(function (out) { return Array.isArray(out) ? out[0] : out; });
      })
      .then(function (blob) {
        if (!blob || !blob.size) throw new Error('empty result');
        var name = String(file.name || 'photo').replace(/\.(heic|heif)$/i, '') + '.jpg';
        return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
      });
  }

  /* ---------- customer-facing message ---------------------------------- */

  function noticeBox(root) {
    var box = root.parentNode.querySelector('.kei-heic-notice');
    if (!box) {
      box = document.createElement('div');
      box.className = 'kei-heic-notice';
      box.setAttribute('role', 'status');
      box.setAttribute('aria-live', 'polite');
      box.style.cssText =
        'margin:8px 0 0;padding:10px 12px;border-radius:8px;font-size:14px;line-height:1.45;display:none';
      root.parentNode.insertBefore(box, root.nextSibling);
    }
    return box;
  }

  function notice(root, text, kind) {
    var box = noticeBox(root);
    if (!text) { box.style.display = 'none'; box.textContent = ''; return; }
    box.textContent = text;
    box.style.display = 'block';
    if (kind === 'error') {
      box.style.background = '#FDECEC';
      box.style.color = '#8A1C1C';
      box.style.border = '1px solid #F3C2C2';
    } else {
      box.style.background = '#EAF1FF';
      box.style.color = '#0B1D34';
      box.style.border = '1px solid #B8D4FF';
    }
  }

  /* ---------- wiring ---------------------------------------------------- */

  function pondFor(root) {
    try { return (window.FilePond && root) ? window.FilePond.find(root) : null; }
    catch (e) { return null; }
  }

  function handle(root, files) {
    var pond = pondFor(root);
    if (!pond) return false;

    var heics = [], others = [];
    for (var i = 0; i < files.length; i++) {
      (looksHeic(files[i]) ? heics : others).push(files[i]);
    }
    if (!heics.length) return false;   // nothing for us — let the app do its job

    others.forEach(function (f) { try { pond.addFile(f); } catch (e) {} });

    notice(root, heics.length > 1
      ? 'Converting ' + heics.length + ' iPhone photos — one moment…'
      : 'Converting your iPhone photo — one moment…', 'info');

    Promise.all(heics.map(function (f) {
      return toJpeg(f)
        .then(function (jpg) { return pond.addFile(jpg); })
        .then(function () { return null; })
        .catch(function () { return f.name || 'photo'; });
    })).then(function (results) {
      var failed = results.filter(Boolean);
      if (!failed.length) {
        notice(root, '');
        return;
      }
      notice(root,
        'We could not read ' + failed.join(', ') + '. On your iPhone, open the photo, ' +
        'tap Share → Save to Files and pick JPEG, then add it here again. ' +
        'Picking straight from your Photos app also works.', 'error');
    });

    return true;
  }

  // Capture phase: this runs before FilePond's own listener, so a HEIC never
  // reaches the code that drops it.
  document.addEventListener('change', function (e) {
    try {
      var input = e.target;
      if (!input || input.type !== 'file' || !input.matches(SEL_INPUT)) return;
      var files = Array.prototype.slice.call(input.files || []);
      if (!files.length || !files.some(looksHeic)) return;
      var root = input.closest(SEL_ROOT);
      if (!root) return;
      if (handle(root, files)) {
        e.stopImmediatePropagation();
        e.preventDefault();
        input.value = '';
      }
    } catch (err) { /* fall through to the app's normal behaviour */ }
  }, true);

  document.addEventListener('drop', function (e) {
    try {
      var root = e.target && e.target.closest && e.target.closest(SEL_ROOT);
      if (!root || !e.dataTransfer) return;
      var files = Array.prototype.slice.call(e.dataTransfer.files || []);
      if (!files.length || !files.some(looksHeic)) return;
      if (handle(root, files)) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    } catch (err) { /* fall through */ }
  }, true);
})();
